import { useState } from 'react';
import { CircleAlert } from 'lucide-react';
import { Badge, Button, ScrollArea } from 'shared-ui';

import { DateField } from '@/components/shared/DateField';
import { fetchVehicleTripTrack } from '@/api/vehiclesApi';
import { showErrorToast } from '@/errors/showErrorToast';
import { toAppError } from '@/errors/toAppError';
import { useVehicleTrips } from '@/hooks/useVehicleTrips';
import type { DisplayedTripTrack } from '@/hooks/useVehicleTripRouteLayer';
import {
    formatDistance,
    formatDuration,
    formatLondonDate,
    formatLondonTime,
    formatTrackStatusLabel,
} from '../utils/vanTripFormat';

// The trips API rejects ranges wider than 31 inclusive calendar days, i.e. the two
// ends may be at most 30 days apart.
const MAX_RANGE_DAY_OFFSET = 30;

/** Adds whole days to a yyyy-MM-dd string. UTC arithmetic, so immune to the browser's zone. */
function addIsoDays(iso: string, days: number): string {
    const [year, month, day] = iso.split('-').map(Number);
    return new Date(Date.UTC(year, month - 1, day + days)).toISOString().slice(0, 10);
}

/** The Pipelines tab's info-banner treatment, so empty states read the same everywhere. */
function TripsNotice({ title, body }: { title: string; body: string }) {
    return (
        <div className="border-origin-blue bg-origin-blue/8 flex gap-3 rounded-lg border px-4 py-3">
            <CircleAlert className="text-origin-blue mt-0.5 size-4 shrink-0" aria-hidden="true" />
            <div>
                <p className="text-origin-blue text-sm leading-5 font-bold">{title}</p>
                <p className="text-origin-blue text-sm leading-5 font-normal">{body}</p>
            </div>
        </div>
    );
}

interface VanTripsTabProps {
    vehicleId: string;
    /** Lazy activation: nothing loads until the user first opens the Trips tab. */
    enabled: boolean;
    onForbidden?: () => void;
    /** Hands a fetched trip track up for the map to draw (route line or location markers). */
    onShowTrack?: (displayed: DisplayedTripTrack) => void;
}

/**
 * Completed-trip history for the selected van. Dates are inclusive Europe/London calendar
 * days (the API's operational-day semantics); displayed times are London too, never the
 * browser's zone. The range starts empty by design — the user chooses it, and nothing is
 * fetched until both ends are set (the hook stands down on an incomplete range).
 */
export function VanTripsTab({ vehicleId, enabled, onForbidden, onShowTrack }: VanTripsTabProps) {
    const [to, setTo] = useState('');
    const [from, setFrom] = useState('');
    const [fetchingTrackTripId, setFetchingTrackTripId] = useState<string | null>(null);

    const { trips, isLoading, isError, retry } = useVehicleTrips(vehicleId, from, to, enabled, onForbidden);

    const showTrack = async (tripId: string) => {
        setFetchingTrackTripId(tripId);
        try {
            const track = await fetchVehicleTripTrack(vehicleId, tripId);
            onShowTrack?.({ tripId, track });
        } catch (error) {
            if (toAppError(error).code === 'forbidden') {
                onForbidden?.();
            } else {
                showErrorToast(error);
            }
        } finally {
            setFetchingTrackTripId(null);
        }
    };

    return (
        <div className="flex min-h-0 flex-1 flex-col">
            <div className="grid shrink-0 grid-cols-2 gap-3 px-6 py-3">
                <DateField
                    id="van-trips-date-from"
                    label="Date From"
                    placeholder="Choose Date"
                    value={from}
                    onChange={(v) => {
                        // Clearing is allowed — an empty end returns the tab to its
                        // choose-a-range prompt. Picking a date drags the other end along
                        // if needed, so a set range can never invert or exceed the
                        // API's maximum width.
                        setFrom(v ?? '');
                        if (v && to) {
                            if (v > to) setTo(v);
                            else if (to > addIsoDays(v, MAX_RANGE_DAY_OFFSET))
                                setTo(addIsoDays(v, MAX_RANGE_DAY_OFFSET));
                        }
                    }}
                />
                <DateField
                    id="van-trips-date-to"
                    label="Date To"
                    placeholder="Choose Date"
                    value={to}
                    onChange={(v) => {
                        setTo(v ?? '');
                        if (v && from) {
                            if (v < from) setFrom(v);
                            else if (from < addIsoDays(v, -MAX_RANGE_DAY_OFFSET))
                                setFrom(addIsoDays(v, -MAX_RANGE_DAY_OFFSET));
                        }
                    }}
                />
            </div>
            <ScrollArea className="min-h-0 flex-1">
                <div className="space-y-3 px-6 pb-4">
                    {isLoading && (
                        <div role="status" aria-label="Loading trips" className="space-y-3">
                            <div className="bg-muted h-16 w-full animate-pulse rounded" />
                            <div className="bg-muted h-16 w-full animate-pulse rounded" />
                        </div>
                    )}
                    {isError && (
                        <div className="space-y-2">
                            <p className="text-destructive text-sm">Unable to load trips — please try again.</p>
                            <Button variant="outline" size="sm" onClick={retry}>
                                Try Again
                            </Button>
                        </div>
                    )}
                    {/* No range chosen yet (the only way trips is null while neither
                        loading nor errored) — prompt for the filters. */}
                    {trips === null && !isLoading && !isError && (
                        <TripsNotice
                            title="No Trips Recorded"
                            body="Choose a Date From and Date To to view this van's trips"
                        />
                    )}
                    {trips?.length === 0 && (
                        <TripsNotice
                            title="No Trips Recorded in this date range"
                            body="There are no trips for this van between the chosen dates — try a wider range"
                        />
                    )}
                    {trips?.map((trip) => (
                        <div key={trip.id} className="border-border/50 rounded-lg border px-3 py-2">
                            <div className="flex items-center justify-between gap-2">
                                <p className="text-foreground text-sm font-semibold">
                                    {formatLondonDate(trip.endTime)}
                                </p>
                                <Badge variant="outline">{formatTrackStatusLabel(trip.trackStatus)}</Badge>
                            </div>
                            <p className="text-muted-foreground mt-0.5 text-xs">
                                {formatLondonTime(trip.startTime)} – {formatLondonTime(trip.endTime)} ·{' '}
                                {formatDistance(trip.distanceM)} · {formatDuration(trip.durationS)}
                            </p>
                            {(trip.startDescription ?? trip.endDescription) && (
                                <p className="text-foreground mt-1 text-xs leading-tight">
                                    {trip.startDescription ?? 'Unknown'} → {trip.endDescription ?? 'Unknown'}
                                </p>
                            )}
                            {trip.operativeName && (
                                <p className="text-muted-foreground mt-0.5 text-xs">{trip.operativeName}</p>
                            )}
                            {/* §10.3 affordances: a real route or honest markers — never a fabricated line */}
                            {(trip.trackStatus === 'route_available' || trip.trackStatus === 'summary_only') && (
                                <Button
                                    variant="outline"
                                    size="sm"
                                    className="mt-2"
                                    disabled={fetchingTrackTripId === trip.id}
                                    onClick={() => {
                                        void showTrack(trip.id);
                                    }}
                                >
                                    {trip.trackStatus === 'route_available' ? 'View route' : 'View trip locations'}
                                </Button>
                            )}
                        </div>
                    ))}
                </div>
            </ScrollArea>
        </div>
    );
}
